import React, { useState, useEffect } from "react";
import api from "./api";
import Joi from "joi";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaCheck,
  FaSearch,
} from "react-icons/fa";

const addSchema = Joi.object({
  companyName: Joi.string().required().messages({
    "string.empty": "Naziv firme je obavezan.",
  }),
  vatId: Joi.string().pattern(/^\d+$/).length(9).required().messages({
    "string.empty": "PIB je obavezan.",
    "string.pattern.base": "PIB mora biti numericki.",
    "string.length": "PIB mora imati tacno 9 cifara.",
  }),
  apiKey: Joi.string().guid({ version: ["uuidv4"] }).required().messages({
    "string.empty": "API kljuc je obavezan.",
    "string.guid": "API kljuc mora biti validan UUID.",
  }),
  pdv: Joi.boolean().required(),
});

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingField, setEditingField] = useState(null); // "password" or "apiKey"
  const [newPassword, setNewPassword] = useState("");
  const [newApiKey, setNewApiKey] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [copiedUser, setCopiedUser] = useState(null);
  const [search, setSearch] = useState("");
  const [copiedAll, setCopiedAll] = useState(false);

  // Add form state
  const [companyName, setCompanyName] = useState("");
  const [vatId, setVatId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [pdv, setPdv] = useState(false);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get("/api/manageUsersFun");
      setUsers(res.data);
    } catch (err) {
      setMessage({ text: "Greska pri ucitavanju korisnika", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (name) => {
    if (!window.confirm(`Da li ste sigurni da zelite da obrisete "${name}"?\n\nOva akcija se ne moze ponistiti.`)) return;
    try {
      await api.delete(`/api/manageUsersFun?companyName=${encodeURIComponent(name)}`);
      setMessage({ text: `${name} uspesno obrisana`, type: "success" });
      loadUsers();
    } catch (err) {
      setMessage({
        text: err.response?.data?.message || "Greska pri brisanju",
        type: "error",
      });
    }
  };

  const handleSave = async (name) => {
    const payload = { companyName: name };
    if (editingField === "password") {
      if (!newPassword || newPassword.length < 4) {
        setMessage({ text: "Lozinka mora imati najmanje 4 karaktera", type: "error" });
        return;
      }
      payload.newPassword = newPassword;
    } else if (editingField === "apiKey") {
      if (!newApiKey.trim()) {
        setMessage({ text: "API kljuc ne moze biti prazan", type: "error" });
        return;
      }
      payload.newApiKey = newApiKey;
    }
    try {
      const res = await api.put("/api/manageUsersFun", payload);
      setMessage({ text: res.data?.message || "Uspesno sacuvano", type: "success" });
      cancelEdit();
      loadUsers();
    } catch (err) {
      setMessage({
        text: err.response?.data?.message || "Greska pri cuvanju",
        type: "error",
      });
    }
  };

  const startEdit = (companyName, field, currentValue) => {
    setEditingUser(companyName);
    setEditingField(field);
    if (field === "password") setNewPassword(currentValue || "");
    if (field === "apiKey") setNewApiKey(currentValue || "");
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditingField(null);
    setNewPassword("");
    setNewApiKey("");
  };

  const handleCopyCredentials = (user) => {
    const text = `Korisnicko ime: ${user.username}\nLozinka: ${user.password}`;
    navigator.clipboard.writeText(text);
    setCopiedUser(user.companyName);
    setTimeout(() => setCopiedUser(null), 2000);
  };

  const filteredUsers = users.filter(
    (u) =>
      u.companyName.toLowerCase().includes(search.toLowerCase()) ||
      u.username.toLowerCase().includes(search.toLowerCase())
  );

  const handleCopyAllCredentials = () => {
    const text = users
      .map((u) => `Firma: ${u.companyName} | Korisnik: ${u.username} | Lozinka: ${u.password}`)
      .join("\n");
    navigator.clipboard.writeText(text);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const validateAddForm = () => {
    const { error } = addSchema.validate(
      { companyName, vatId, apiKey, pdv },
      { abortEarly: false }
    );
    if (!error) {
      setAddErrors({});
      return true;
    }
    const errs = {};
    error.details.forEach((d) => (errs[d.path[0]] = d.message));
    setAddErrors(errs);
    return false;
  };

  const handleAddCompany = async () => {
    if (!validateAddForm()) return;
    setAddLoading(true);
    try {
      const res = await api.post("/api/addNewCompanyFun", {
        companyName,
        vatId,
        apiKey,
        pdv,
      });
      setMessage({
        text: res.data?.message || "Firma uspesno dodata!",
        type: "success",
      });
      setCompanyName("");
      setVatId("");
      setApiKey("");
      setPdv(false);
      setShowAddForm(false);
      loadUsers();
    } catch (err) {
      setMessage({
        text: err.response?.data?.message || "Greska pri dodavanju firme",
        type: "error",
      });
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-card page-card-wide">
        <div style={{ padding: 20 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} className="skeleton" style={{ height: 40, marginBottom: 8 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-card page-card-wide">
      <div className="page-title-row">
        <h2 className="page-title" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
          Upravljanje korisnicima
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <FaSearch style={{ position: "absolute", left: 10, color: "var(--text-muted)", fontSize: 13 }} />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Pretrazi korisnike..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30, width: 200 }}
            />
          </div>
          <button
            className="btn-sm btn-outline"
            onClick={() => setShowPasswords(!showPasswords)}
            title={showPasswords ? "Sakrij lozinke" : "Prikazi lozinke"}
          >
            {showPasswords ? <FaEyeSlash /> : <FaEye />}
            <span>{showPasswords ? "Sakrij" : "Prikazi"} lozinke</span>
          </button>
          <button
            className="btn-sm btn-outline"
            onClick={handleCopyAllCredentials}
            title="Kopiraj sve kredencijale"
          >
            {copiedAll ? <FaCheck /> : <FaCopy />}
            <span>{copiedAll ? "Kopirano!" : "Kopiraj sve"}</span>
          </button>
          <button
            className="btn-sm btn-success"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <FaPlus />
            <span>Nova firma</span>
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`status-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {showAddForm && (
        <div className="add-form-card">
          <h3 style={{ margin: "0 0 16px", fontSize: "1rem" }}>Dodaj novu firmu</h3>
          <div className="add-form-grid">
            <div className="form-group">
              <label>Naziv firme</label>
              <input
                type="text"
                className={`form-control ${addErrors.companyName ? "input-error" : ""}`}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="npr. Moja Firma DOO"
              />
              {addErrors.companyName && (
                <small className="error-text">{addErrors.companyName}</small>
              )}
            </div>
            <div className="form-group">
              <label>PIB (9 cifara)</label>
              <input
                type="text"
                className={`form-control ${addErrors.vatId ? "input-error" : ""}`}
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
                placeholder="npr. 123456789"
                maxLength={9}
              />
              {addErrors.vatId && (
                <small className="error-text">{addErrors.vatId}</small>
              )}
            </div>
            <div className="form-group">
              <label>API kljuc (UUID)</label>
              <input
                type="text"
                className={`form-control ${addErrors.apiKey ? "input-error" : ""}`}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="npr. a1b2c3d4-e5f6-7890-abcd-ef1234567890"
              />
              {addErrors.apiKey && (
                <small className="error-text">{addErrors.apiKey}</small>
              )}
            </div>
            <div className="form-group" style={{ display: "flex", alignItems: "flex-end" }}>
              <label className="checkbox-label" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={pdv}
                  onChange={() => setPdv(!pdv)}
                />
                PDV obveznik
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              className="btn-primary"
              onClick={handleAddCompany}
              disabled={addLoading}
              style={{ flex: 1, maxWidth: 200 }}
            >
              {addLoading ? "Dodavanje..." : "Dodaj firmu"}
            </button>
            <button
              className="btn-secondary"
              onClick={() => {
                setShowAddForm(false);
                setAddErrors({});
              }}
            >
              Otkazi
            </button>
          </div>
        </div>
      )}

      <div className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th style={{ width: "1%", whiteSpace: "nowrap" }}>Firma</th>
              <th>Korisnik</th>
              <th>Lozinka</th>
              <th>API kljuc</th>
              <th>PDV</th>
              <th>Status</th>
              <th>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u._id} className={u.role === "admin" ? "row-admin" : ""}>
                <td style={{ whiteSpace: "nowrap" }}>{u.companyName}</td>
                <td><code>{u.username}</code></td>
                <td>
                  {editingUser === u.companyName && editingField === "password" ? (
                    <div className="edit-inline-row">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nova lozinka"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSave(u.companyName);
                          if (e.key === "Escape") cancelEdit();
                        }}
                      />
                      <button className="btn-icon btn-icon-success" onClick={() => handleSave(u.companyName)} title="Sacuvaj">
                        <FaSave />
                      </button>
                      <button className="btn-icon btn-icon-muted" onClick={cancelEdit} title="Otkazi">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <span
                      className="editable-cell"
                      onClick={() => startEdit(u.companyName, "password", u.password)}
                      title="Kliknite za izmenu"
                    >
                      {showPasswords ? (
                        <code>{u.password || "—"}</code>
                      ) : (
                        <span className="password-dots">••••••</span>
                      )}
                      <FaEdit className="edit-hint-icon" />
                    </span>
                  )}
                </td>
                <td>
                  {editingUser === u.companyName && editingField === "apiKey" ? (
                    <div className="edit-inline-row">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newApiKey}
                        onChange={(e) => setNewApiKey(e.target.value)}
                        placeholder="Novi API kljuc"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSave(u.companyName);
                          if (e.key === "Escape") cancelEdit();
                        }}
                        style={{ minWidth: 200 }}
                      />
                      <button className="btn-icon btn-icon-success" onClick={() => handleSave(u.companyName)} title="Sacuvaj">
                        <FaSave />
                      </button>
                      <button className="btn-icon btn-icon-muted" onClick={cancelEdit} title="Otkazi">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <span
                      className="editable-cell"
                      onClick={() => startEdit(u.companyName, "apiKey", u.apiKey)}
                      title="Kliknite za izmenu"
                    >
                      <code className="api-key-code">{u.apiKey ? `${u.apiKey.slice(0, 8)}...` : "—"}</code>
                      <FaEdit className="edit-hint-icon" />
                    </span>
                  )}
                </td>
                <td>{u.pdv ? "Da" : "Ne"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {u.role === "admin" ? (
                    <span style={{ color: "var(--text-muted, #888)" }}>—</span>
                  ) : u.lastScrapeError ? (
                    <span style={{ color: "var(--danger, #c0392b)" }} title={u.lastScrapeError}>
                      ● Greška
                    </span>
                  ) : u.lastScrapeAt ? (
                    <span
                      style={{ color: "var(--success, #27ae60)" }}
                      title={`Poslednje ažuriranje: ${new Date(u.lastScrapeAt).toLocaleString("sr-RS")}`}
                    >
                      ● {new Date(u.lastScrapeAt).toLocaleDateString("sr-RS")}
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-muted, #888)" }} title="Još nije ažurirano">—</span>
                  )}
                </td>
                <td>
                  {!(editingUser === u.companyName) && (
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-icon-copy"
                        onClick={() => handleCopyCredentials(u)}
                        title="Kopiraj kredencijale"
                      >
                        {copiedUser === u.companyName ? <FaCheck /> : <FaCopy />}
                      </button>
                      {u.role !== "admin" && (
                        <button
                          className="btn-icon btn-icon-delete"
                          onClick={() => handleDeleteUser(u.companyName)}
                          title="Obrisi firmu"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="users-count">
        {search
          ? `Prikazano: ${filteredUsers.length} od ${users.length}`
          : `Ukupno: ${users.length} ${users.length === 1 ? "korisnik" : "korisnika"}`}
      </p>
    </div>
  );
}

export default UsersPage;
